import { Flex } from '@revenuemarket/cds';
import { colorTokens, semanticColors } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { ArrowCircle, DocumentArrowUp, DocumentArrowDown } from '@revenuemarket/ui';
import { formatBusinessNumber } from '@revenuemarket/utils';
import { Badge, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';

import SettlementInfoModal from '~components/Settlement/SettlementInfo/SettlementInfoModal';
import { SettlementStatus, getStautsForBadge } from '~components/Settlement/utils';
import { CashFlowType } from '~models/cashFlow';
import { TaxInvoiceType } from '~models/tax-invoice';
export interface SettlementTransaction {
  cashFlowType: CashFlowType;
  transactionAt: string;
  transactionDescription: string;
  transactionName: string;
  mappingAmount: number;
  amount: number;
  mode?: 'summary' | 'detail';
  active?: boolean;
  type?: 'minus' | 'plus';
  additionalInfo?: React.ReactNode;
  currencyCode?: string;
}

export const displayAmount = (amount: number, currencyCode?: string) => {
  return currencyCode === 'KRW' || !currencyCode
    ? amount.toLocaleString() + '원'
    : `${currencyCode} ${amount.toLocaleString()}`;
};

export const TransactionMappingItem: React.FC<SettlementTransaction> = ({
  cashFlowType,
  mode = 'detail',
  transactionName,
  transactionDescription,
  active,
  amount,
  mappingAmount,
  transactionAt,
  additionalInfo,
  currencyCode,
}) => {
  return (
    <Flex direction="column" gap={12}>
      <Flex justify="space-between" align="center">
        <Flex align="center" gap={12}>
          {cashFlowType === CashFlowType.IN ? (
            <Flex align="center" gap={4}>
              <ArrowCircle fill={'#4DC987'} />
              <Typography.Text>입금</Typography.Text>
            </Flex>
          ) : (
            <Flex align="center" gap={4}>
              <ArrowCircle
                fill={'#FF7A70'}
                style={{
                  transform: 'rotate(180deg)',
                }}
              />
              <Typography.Text>출금</Typography.Text>
            </Flex>
          )}
          <Typography.Text
            style={{
              color: colorTokens.textDescription,
            }}
          >
            {dayjs(transactionAt).format('M월 D일')}
          </Typography.Text>
        </Flex>
        {additionalInfo}
      </Flex>
      {mode === 'detail' && (
        <Flex align="flex-start" justify="space-between">
          <Flex direction="column" gap={4}>
            <Typography.Text
              style={{
                fontSize: 14,
              }}
            >
              {transactionDescription}
            </Typography.Text>
            <Typography.Text
              style={{
                color: colorTokens.textDescription,
              }}
            >
              {transactionName}
            </Typography.Text>
          </Flex>
          {mappingAmount === amount ? (
            <Typography.Text
              style={{
                fontWeight: active ? 'bold' : 'normal',
                fontSize: 14,
                textAlign: 'right',
              }}
            >
              {displayAmount(amount, currencyCode)}
            </Typography.Text>
          ) : (
            <Flex direction="column" gap={4}>
              <Typography.Text
                style={{
                  fontSize: 14,
                  textAlign: 'right',
                }}
              >
                {displayAmount(mappingAmount, currencyCode)}
              </Typography.Text>
              <Typography.Text style={{ color: colorTokens.textDescription }}>{`/${displayAmount(
                amount,
                currencyCode,
              )}`}</Typography.Text>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export interface SettlementTaxInvoice {
  type: TaxInvoiceType;
  date: string;
  status?: SettlementStatus;
  businessRegistrationNumber: string;
  contractorName: string;
  counterPartyName: string;
  mappingAmount: number;
  totalAmount: number;
  repItem: string;
  active?: boolean;
  icon?: React.ReactNode;
  additionalInfo?: React.ReactNode;
  businessEntityId?: number | null;
  onMappingRefresh?: () => void;
}

export const TaxInvoiceMappingItem: React.FC<SettlementTaxInvoice> = ({
  type,
  date,
  status,
  contractorName,
  counterPartyName,
  businessRegistrationNumber,
  businessEntityId,
  active,
  totalAmount,
  mappingAmount,
  repItem,
  icon,
  additionalInfo,
  onMappingRefresh,
}) => {
  return (
    <Flex
      direction="column"
      gap={12}
      style={{
        width: '100%',
      }}
    >
      <Flex justify="space-between" align="center">
        <Flex align="center" gap={12}>
          <Flex align="center" gap={4}>
            {icon}
            {type === 'SALES' ? (
              <DocumentArrowUp fill={'#1A965C'} width={20} />
            ) : (
              <DocumentArrowDown fill={'#D13030'} width={20} />
            )}
            <Typography.Text>{type === 'PURCHASE' ? '매입' : '매출'}</Typography.Text>
          </Flex>
          <Typography.Text
            style={{
              color: colorTokens.textDescription,
            }}
          >
            {dayjs(date).format('M월 D일')}
          </Typography.Text>
        </Flex>
        {additionalInfo}
        {status && (
          <Badge
            status={getStautsForBadge(status).status}
            text={`정산 ${getStautsForBadge(status).text}`}
          />
        )}
      </Flex>
      <Flex justify="space-between" align="flex-start">
        <Flex direction="column">
          {businessEntityId ? (
            <a
              style={{ fontSize: 14, maxWidth: 190, textDecoration: 'underline' }}
              onClick={() => {
                if (!businessEntityId) {
                  return;
                }
                overlays.open(({ isOpen, onClose }) => (
                  <SettlementInfoModal
                    onCancel={() => {
                      onMappingRefresh?.();
                      onClose();
                    }}
                    open={isOpen}
                    settlementType={type}
                    defaultBusinessEntityId={businessEntityId}
                  />
                ));
              }}
            >
              {contractorName}
            </a>
          ) : (
            <Typography.Text
              style={{
                fontSize: 14,
                maxWidth: 190,
              }}
            >
              {contractorName}
            </Typography.Text>
          )}
          <Typography.Text
            style={{
              color: colorTokens.textDescription,
            }}
          >
            {!active
              ? `${formatBusinessNumber(businessRegistrationNumber)} ${
                  counterPartyName ? `| ${counterPartyName}` : ''
                } ${repItem ? `| ${repItem}` : ''}`
              : `${formatBusinessNumber(businessRegistrationNumber)}  ${
                  counterPartyName ? `| ${counterPartyName}` : ''
                } `}
          </Typography.Text>
        </Flex>
        {active ? (
          <Flex direction="column" gap={4}>
            <Typography.Text
              style={{
                fontWeight: 'bold',
                fontSize: 14,
              }}
            >
              {totalAmount.toLocaleString() + '원'}
            </Typography.Text>
            <Typography.Text>{repItem}</Typography.Text>
          </Flex>
        ) : (
          <Flex direction="column" gap={4}>
            {mappingAmount !== totalAmount ? (
              <>
                <Typography.Text
                  style={{
                    fontSize: 14,
                  }}
                >
                  {mappingAmount.toLocaleString() + '원'}
                </Typography.Text>
                <Typography.Text
                  style={{
                    color: colorTokens.textDescription,
                  }}
                >{`/${totalAmount.toLocaleString() + '원'}`}</Typography.Text>
              </>
            ) : (
              <Typography.Text
                style={{
                  fontSize: 14,
                }}
              >
                {totalAmount.toLocaleString() + '원'}
              </Typography.Text>
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

interface TotalProps {
  mappingAmount?: number;
  unMappingAmount: number;
  mappingAmountLabel?: React.ReactNode;
  unmappingAmountLabel: React.ReactNode;
  unmappingAmountError?: boolean;
  unmappingErrorText?: string;
  currencyCode?: string;
}

export const MappingTotal: React.FC<TotalProps> = ({
  mappingAmount,
  unMappingAmount,
  mappingAmountLabel,
  unmappingAmountLabel,
  unmappingAmountError,
  unmappingErrorText,
  currencyCode,
}) => {
  return (
    <Flex
      style={{
        padding: '8px 16px',
        background: ' rgba(0,0,0,0.04)',
        border: '1px solid rgba(0,0,0,0.06)',
        borderRadius: 8,
      }}
      direction="column"
      gap={mappingAmount ? 8 : 0}
    >
      {mappingAmountLabel ? (
        <Flex justify="space-between">
          <Typography.Text
            style={{
              fontSize: 14,
            }}
          >
            {mappingAmountLabel}
          </Typography.Text>
          <Typography.Text
            style={{
              fontSize: 14,
            }}
          >
            {displayAmount(mappingAmount ?? 0, currencyCode)}
          </Typography.Text>
        </Flex>
      ) : (
        <div />
      )}
      <Flex justify="space-between">
        {' '}
        <Typography.Text
          style={{
            fontSize: 14,
            color: unMappingAmount !== 0 ? semanticColors.danger.medium : '',
          }}
        >
          {unmappingAmountLabel}
        </Typography.Text>
        {unmappingErrorText ? (
          <Tooltip title={unmappingErrorText}>
            <Typography.Text
              style={{
                fontSize: 14,
                color: unmappingAmountError ? colorTokens.textDanger : '',
              }}
            >
              {displayAmount(unMappingAmount, currencyCode)}
            </Typography.Text>
          </Tooltip>
        ) : (
          <Typography.Text
            style={{
              fontSize: 14,
              color: unmappingAmountError ? colorTokens.textDanger : '',
            }}
          >
            {displayAmount(unMappingAmount, currencyCode)}
          </Typography.Text>
        )}
      </Flex>
    </Flex>
  );
};
